import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, Col, Container, Row, Form, Offcanvas } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  LayersControl,
  LayerGroup,
  useMap,
  ZoomControl,
} from "react-leaflet";
import * as L from "leaflet";
import Supercluster from "supercluster";
import "leaflet/dist/leaflet.css";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useBackgroundProjects, useBackgroundTrees } from "@hooks/useBackgroundData";
import useWindowDimensions from "@hooks/useWindowDimension";
import Spinner from "@components/Spinner/Spinner";
import TreeDetail from "@modules/Trees/components/TreeDetail";
import TreeForm from "@modules/Trees/components/Form/AddForm";
import { getTree } from "@modules/Trees/services/treeCrud";
import { AngleRightIcon, TriangleUpIcon, CrosshairIcon } from "@components/icons";
import { DARKGREEN } from "@helper/colors";
import { filterTreesByProject } from "@helper/projectFilter";
import {
  parentTreeIcon,
  noSensorTreeIcon,
  unknownNoDataIcon,
  unknownMaintenanceIcon,
} from "@helper/marker";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import { formInitialValues } from "@modules/Trees/components/Form/AddForm/FormModel/initialValues";
import {
  createIrrigationArea,
  getAllIrrigationAreas,
} from "@modules/IrrigationArea/services/areaCrud";
import { createProject, getPublicProject } from "@modules/Projects/services/projectCrud";
import {
  mapCanvasStyle,
  mapFloatingPrimaryButtonStyle,
  mapOverlayInputStyle,
  mapPanelStyle,
  mapPrimaryButtonStyle,
  mapSecondaryButtonStyle,
} from "./mapViewStyles";
import { OpenStreetMapProvider } from "leaflet-geosearch";

function MapRefBinder({ mapRef, onReady }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    mapRef.current = map;
    onReady?.(map);
  }, [map, mapRef, onReady]);

  return null;
}

const isSameLatLng = (a, b) => {
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 0.00001 && Math.abs(a.lng - b.lng) < 0.00001;
};

const formatCoordinateValue = (value, digits = 6) => {
  if (value == null || value === "") return "–";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "–";
  return numericValue.toFixed(digits);
};

function CenterPositionUpdater({ enabled, onChange }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled) return;
    const updateCenter = () => {
      const center = map.getCenter();
      onChange(center);
    };

    updateCenter();
    map.on("moveend", updateCenter);

    return () => {
      map.off("moveend", updateCenter);
    };
  }, [enabled, map, onChange]);

  return null;
}

const geoSearchProvider = new OpenStreetMapProvider();

function AddressSearchInput({ onSelect, placeholder, disabled, mapRef }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      const searchResults = await geoSearchProvider.search({ query: searchQuery });
      setResults(searchResults.slice(0, 5));
      setIsOpen(searchResults.length > 0);
    } catch (err) {
      console.error("Address search error:", err);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(value), 300);
  };

  const handleSelect = (result) => {
    const latlng = { lat: result.y, lng: result.x };
    onSelect(latlng);
    setQuery(result.label);
    setIsOpen(false);
    if (mapRef?.current) {
      mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 18));
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", flex: 1 }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "6px 10px",
          borderRadius: "6px",
          fontSize: "0.875rem",
          background: "var(--color-bg-surface, #ffffff)",
          color: "var(--color-text-primary, #0e1d2c)",
          border: "1px solid var(--color-border-default, #b8ccda)",
        }}
      />
      {isOpen && results.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            margin: 0,
            marginTop: "4px",
            padding: 0,
            listStyle: "none",
            background: "var(--color-bg-surface, #ffffff)",
            border: "1px solid var(--color-border-default, #b8ccda)",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {results.map((result, idx) => (
            <li
              key={idx}
              onClick={() => handleSelect(result)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "0.8125rem",
                color: "var(--color-text-primary, #0e1d2c)",
                borderBottom:
                  idx < results.length - 1
                    ? "1px solid var(--color-border-subtle, #d8e4ea)"
                    : "none",
              }}
              onMouseEnter={(e) => (e.target.style.background = "var(--color-bg-subtle, #f7f9fa)")}
              onMouseLeave={(e) => (e.target.style.background = "transparent")}
            >
              {result.label}
            </li>
          ))}
        </ul>
      )}
      {isLoading && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
          <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary, #5a7a8f)" }}>
            ...
          </span>
        </div>
      )}
    </div>
  );
}

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const normalized = value.replace(",", ".").trim();
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

// Normalize any backend/frontend position shape into a Leaflet LatLng.
// Accepts decimal commas, arrays, "lat,lng" strings, whitespace-separated pairs, GeoJSON, and plain objects.
const toLatLng = (position) => {
  if (!position) return null;

  const asPair = (a, b) => {
    const lat = toNumber(a);
    const lng = toNumber(b);
    return lat != null && lng != null ? L.latLng(lat, lng) : null;
  };

  if (Array.isArray(position) && position.length >= 2) {
    return asPair(position[0], position[1]);
  }

  if (typeof position === "string") {
    const parts = position.trim().replace(/[;,]/g, " ").split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return asPair(parts[0], parts[1]);
    }
  }

  if (typeof position === "object") {
    if (position.latlng) return toLatLng(position.latlng);

    if (position.position) return toLatLng(position.position);

    if (Array.isArray(position.coordinates) && position.coordinates.length >= 2) {
      const [lng, lat] = position.coordinates;
      return asPair(lat, lng);
    }

    if (position[0] !== undefined && position[1] !== undefined) {
      return asPair(position[0], position[1]);
    }

    const { lat, lng, latitude, longitude, lon, long, lngs } = position;
    const resolvedLat = lat ?? latitude;
    const resolvedLng = lng ?? longitude ?? lon ?? long ?? lngs;
    return asPair(resolvedLat, resolvedLng);
  }

  return null;
};

const createClusterIcon = (count) => {
  const size = count >= 100 ? 44 : count >= 25 ? 40 : 36;
  const fontSize = count >= 100 ? 13 : 12;

  return L.divIcon({
    className: "treesense-cluster-icon",
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      border-radius: 999px;
      background: ${DARKGREEN};
      border: 2px solid rgba(255, 255, 255, 0.92);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: ${fontSize}px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const resizeTreeMarkerIcon = (icon, newWidth, newHeight) => {
  if (!icon?.options) return icon;
  const oldHtml = icon.options.html || "";
  let newHtml = oldHtml;
  let newAnchor = icon.options.iconAnchor;
  let newSize = icon.options.iconSize;

  if (oldHtml.includes('id="childTree"')) {
    newHtml = oldHtml
      .replace(/width=\d+/, `width=${newWidth / 2}`)
      .replace(/height=\d+/, `height=${newHeight / 2}`)
      .replace(/rx=\d+/, `rx=${newWidth / 2}`)
      .replace(/ry=\d+/, `ry=${newHeight / 2}`);
    newAnchor = [newWidth / 4, newHeight / 4];
    newSize = [newWidth / 2, newHeight / 2];
  } else if (oldHtml.includes("height")) {
    newHtml = oldHtml
      .replace(/width=\d+/, `width=${newWidth}`)
      .replace(/height=\d+/, `height=${newHeight}`);
    newAnchor = [newWidth / 2, newHeight];
    newSize = [newWidth, newHeight];
  } else if (oldHtml.includes("cx")) {
    newHtml = oldHtml
      .replace(/cx="\d+"/, `cx="${newWidth / 8}"`)
      .replace(/cy="\d+"/, `cy="${newHeight / 8}"`)
      .replace(/r="\d+"/, `r="${newHeight / 8}"`);
    newAnchor = [newWidth / 8, newHeight / 8];
    newSize = [newWidth / 4, newHeight / 4];
  }

  return L.divIcon({
    ...icon.options,
    html: newHtml,
    iconSize: newSize,
    iconAnchor: newAnchor,
  });
};

const mapTreeStatusOptions = [
  {
    key: "healthy",
    labelKey: "health_states.healthy",
    iconLabel: "🟢",
    color: "#16a34a",
    match: (tree) => Number(tree.health_state) === 0,
  },
  {
    key: "drying-out",
    labelKey: "health_states.drying_out",
    iconLabel: "🟡",
    color: "#eab308",
    match: (tree) => Number(tree.health_state) === 1,
  },
  {
    key: "dry",
    labelKey: "health_states.dry",
    iconLabel: "🔴",
    color: "#ef4444",
    match: (tree) => Number(tree.health_state) === 2,
  },
  {
    key: "frost",
    labelKey: "health_states.frost",
    iconLabel: "⚪",
    color: "#ffffff",
    dotBorderColor: "#9fb5c5",
    match: (tree) => Number(tree.health_state) === 4,
  },
  {
    key: "too-wet",
    labelKey: "health_states.too_wet",
    iconLabel: "🔵",
    color: "#2563eb",
    match: (tree) => Number(tree.health_state) === 5,
  },
  {
    key: "no-sensor",
    labelKey: "health_states.no_sensor",
    iconLabel: "📡",
    color: "#bdbdbd",
    match: (tree) => Number(tree.health_state) === 3 && !tree.has_active_sensor,
  },
  {
    key: "no-data",
    labelKey: "health_states.no_data",
    iconLabel: "⬜",
    color: "#9e9e9e",
    match: (tree) =>
      Number(tree.health_state) === 3 &&
      tree.has_active_sensor &&
      ![1, 2].includes(Number(tree.active_sensor_state)),
  },
  {
    key: "maintenance-needed",
    labelKey: "health_states.maintenance_needed",
    iconLabel: "🔧",
    color: "#607d8b",
    match: (tree) =>
      Number(tree.health_state) === 3 && [1, 2].includes(Number(tree.active_sensor_state)),
  },
];

function TreeClusterLayer({ trees, setOpenTreePopup, setSelectedTree, selectedTree, tid }) {
  const map = useMap();
  const [clusterFeatures, setClusterFeatures] = useState([]);
  const clusterIconCacheRef = useRef(new Map());

  const treeById = useMemo(() => new Map(trees.map((tree) => [Number(tree.id), tree])), [trees]);

  const points = useMemo(
    () =>
      trees.map((tree) => ({
        type: "Feature",
        properties: {
          cluster: false,
          treeId: Number(tree.id),
        },
        geometry: {
          type: "Point",
          coordinates: [tree.latlng.lng, tree.latlng.lat],
        },
      })),
    [trees]
  );

  // supercluster avoids react-leaflet v4 wrapper incompatibilities and lets us query only viewport data.
  const clusterIndex = useMemo(
    () =>
      new Supercluster({
        radius: 48,
        maxZoom: 17,
      }).load(points),
    [points]
  );

  const getClusterIcon = useCallback((count) => {
    const cacheKey = Number(count) || 0;
    if (!clusterIconCacheRef.current.has(cacheKey)) {
      clusterIconCacheRef.current.set(cacheKey, createClusterIcon(cacheKey));
    }
    return clusterIconCacheRef.current.get(cacheKey);
  }, []);

  const updateClusterFeatures = useCallback(() => {
    const bounds = map.getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    setClusterFeatures(clusterIndex.getClusters(bbox, Math.round(map.getZoom())));
  }, [clusterIndex, map]);

  useEffect(() => {
    updateClusterFeatures();
    map.on("moveend", updateClusterFeatures);
    map.on("zoomend", updateClusterFeatures);

    return () => {
      map.off("moveend", updateClusterFeatures);
      map.off("zoomend", updateClusterFeatures);
    };
  }, [map, updateClusterFeatures]);

  // Ensure markers are visible on initial paint/resizes before user interaction.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => updateClusterFeatures());
    const t1 = setTimeout(() => updateClusterFeatures(), 120);
    const t2 = setTimeout(() => updateClusterFeatures(), 320);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [trees.length, updateClusterFeatures]);

  useEffect(() => {
    if (!tid || selectedTree) return;
    const tree = treeById.get(Number(tid));
    if (!tree) return;
    setSelectedTree(tree);
    setOpenTreePopup(tree.id);
  }, [tid, selectedTree, treeById, setSelectedTree, setOpenTreePopup]);

  useEffect(() => {
    setClusterFeatures([]);
  }, [trees]);

  return clusterFeatures.map((feature) => {
    const properties = feature.properties || {};
    const [lng, lat] = feature.geometry.coordinates;
    const position = [lat, lng];

    if (properties.cluster) {
      const clusterId = properties.cluster_id;
      const count = properties.point_count || 0;
      if (clusterId == null) return null;

      return (
        <Marker
          key={`cluster-${clusterId}`}
          position={position}
          icon={getClusterIcon(count)}
          eventHandlers={{
            click: () => {
              const expansionZoom = Math.min(
                clusterIndex.getClusterExpansionZoom(clusterId),
                map.getMaxZoom()
              );
              map.flyTo(position, expansionZoom);
            },
          }}
        />
      );
    }

    const tree = treeById.get(Number(properties.treeId));
    if (!tree || !tree.latlng) return null;
    const health = tree.health_state;
    const baseIcon = (() => {
      if (health === 3) {
        if (!tree.has_active_sensor) return noSensorTreeIcon();
        const sensorState = tree.active_sensor_state;
        if (sensorState === 1 || sensorState === 2) return unknownMaintenanceIcon();
        return unknownNoDataIcon();
      }
      return tree.has_active_sensor ? parentTreeIcon(health) : noSensorTreeIcon();
    })();
    const isSelected = Number(tree?.id) === Number(selectedTree?.id);
    const markerIcon = isSelected ? resizeTreeMarkerIcon(baseIcon, 80, 80) : baseIcon;

    return (
      <Marker
        key={tree.id}
        position={tree.latlng}
        icon={markerIcon}
        eventHandlers={{
          click: () => {
            setSelectedTree(tree);
            setOpenTreePopup(tree.id);
            map.flyTo(tree.latlng, Math.max(map.getZoom(), 18));
          },
        }}
      />
    );
  });
}

function StandaloneMapView({ topOffset = "4rem", isPublic = false }) {
  const { pid: routeProjectId } = useParams();
  const [openTreePopup, setOpenTreePopup] = useState(null);
  const [selectedTree, setSelectedTree] = useState();
  const [openList, setOpenList] = useState(false);
  const [pendingFlyTreeId, setPendingFlyTreeId] = useState(null);
  const [isAddingTree, setIsAddingTree] = useState(false);
  const [isPositionConfirmed, setIsPositionConfirmed] = useState(false);
  const [addPosition, setAddPosition] = useState(null);
  const [editPosition, setEditPosition] = useState(null);
  const [isEditPositionConfirmed, setIsEditPositionConfirmed] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [selectedProjectIdForArea, setSelectedProjectIdForArea] = useState(null);
  const [isSensorEditOpen, setIsSensorEditOpen] = useState(false);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const tid = query.get("selectedTree");
  const tidNumber = tid ? +tid : null;
  const areaIdParam = query.get("area");
  const isEditMode = query.get("editTree") === "1" || query.get("editTree") === "true";
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const useMapDetailDrawer = width < 992;
  const mapRef = useRef(null);
  const queryClient = useQueryClient();
  const { selectedProjectId: filterProjectId } = useProjectFilter();

  // In public mode, use route param (converted to number); otherwise use filter context
  const selectedProjectId = isPublic
    ? routeProjectId
      ? Number(routeProjectId)
      : null
    : filterProjectId;

  // Helper function to get the correct map base URL - use current pathname to stay on same route
  const getMapUrl = useCallback(() => {
    return location.pathname;
  }, [location.pathname]);
  const [groupedOpen, setGroupedOpen] = useState({});
  const [mapReady, setMapReady] = useState(false);
  const [activeTreeHealthFilters, setActiveTreeHealthFilters] = useState(() =>
    mapTreeStatusOptions.reduce((acc, option) => {
      acc[option.key] = true;
      return acc;
    }, {})
  );
  const [activeSensorFilters, setActiveSensorFilters] = useState({
    withSensor: true,
    withoutSensor: true,
  });
  const [activeAreaFilters, setActiveAreaFilters] = useState({});
  const [areaFilterOpen, setAreaFilterOpen] = useState(false);
  const areaFilterRef = useRef(null);
  const previousSelectionRef = useRef({ openTreePopup: null, selectedTree: null });
  const centerMarkerSvg = useMemo(() => parentTreeIcon(0).options.html, []);
  const centerMarkerTipOffsetY = 8;
  const ignoreNextFormSyncRef = useRef(false);
  const editFlyRef = useRef(false);
  const isEditPositionConfirmedRef = useRef(false);
  const editPositionRef = useRef(null);
  const addDetailPanelRef = useRef(null);
  const editDetailPanelRef = useRef(null);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const mainEl = document.querySelector("main.page-container");

    const prevHtmlOverflow = htmlEl.style.overflow;
    const prevBodyOverflow = bodyEl.style.overflow;
    const prevMainOverflow = mainEl?.style.overflow;

    htmlEl.style.overflow = "hidden";
    bodyEl.style.overflow = "hidden";
    if (mainEl) mainEl.style.overflow = "hidden";

    return () => {
      htmlEl.style.overflow = prevHtmlOverflow;
      bodyEl.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow || "";
    };
  }, []);

  // Public mode: fetch from public API; Authenticated mode: use background data
  const { trees: authTrees, isLoading: authLoading } = useBackgroundTrees(!isPublic);
  const { projects: authProjects } = useBackgroundProjects(!isPublic);

  const {
    data: publicProject,
    isLoading: publicLoading,
    isError: publicError,
  } = useQuery(["publicProject", routeProjectId], () => getPublicProject(routeProjectId), {
    enabled: isPublic && !!routeProjectId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60,
  });

  // Extract trees from public project data
  const publicTrees = useMemo(() => {
    if (!isPublic || !publicProject?.areas) return [];
    return publicProject.areas.flatMap((area) =>
      (area.tree_set || []).map((tree) => ({
        ...tree,
        area_name: area.name,
        area_id: area.id,
        project_id: publicProject.id,
        has_active_sensor: tree.installations?.length > 0,
      }))
    );
  }, [isPublic, publicProject]);

  const allCustomerTrees = isPublic ? publicTrees : authTrees;
  const allProjects = isPublic ? (publicProject ? [publicProject] : []) : authProjects;
  const isLoading = isPublic ? publicLoading : authLoading;
  const noop = () => undefined;
  const buildEditIcon = useCallback((hasSensor, health) => {
    const base = hasSensor ? parentTreeIcon(health) : noSensorTreeIcon();
    const scale = 1.6;
    const baseSize = base?.options?.iconSize || [40, 40];
    const [w, h] = baseSize;
    return L.divIcon({
      ...base.options,
      iconSize: [w * scale, h * scale],
      iconAnchor: [(w * scale) / 2 - 1, h * scale],
      html: `<div style="transform: scale(${scale}); transform-origin: bottom center;">${
        base.options.html || ""
      }</div>`,
    });
  }, []);

  const { data: areaData } = useQuery("areas", getAllIrrigationAreas, {
    enabled: !isPublic,
    refetchOnWindowFocus: false,
  });

  const allAreas = useMemo(() => {
    if (Array.isArray(areaData)) return areaData;
    if (Array.isArray(areaData?.results)) return areaData.results;
    if (Array.isArray(areaData?.data)) return areaData.data;
    return [];
  }, [areaData]);

  const effectiveProjectId = selectedProjectId ?? selectedProjectIdForArea;

  const availableAreas = useMemo(() => {
    if (!effectiveProjectId) return [];
    return allAreas.filter(
      (area) =>
        String(area.project?.id || area.project_id || area.projectId) === String(effectiveProjectId)
    );
  }, [allAreas, effectiveProjectId]);

  const addAreaToCache = useCallback(
    (createdArea) => {
      if (!createdArea) return;
      queryClient.setQueryData("areas", (prev) => {
        if (!prev) return [createdArea];
        if (Array.isArray(prev)) return [...prev, createdArea];
        if (Array.isArray(prev?.results)) {
          return { ...prev, results: [...prev.results, createdArea] };
        }
        if (Array.isArray(prev?.data)) {
          return { ...prev, data: [...prev.data, createdArea] };
        }
        return prev;
      });
    },
    [queryClient]
  );

  const { mutateAsync: createArea, isLoading: isCreatingArea } = useMutation(
    "createAreaQuick",
    createIrrigationArea,
    {
      onSuccess: (createdArea) => {
        addAreaToCache(createdArea);
        if (createdArea?.id) {
          setSelectedAreaId(createdArea.id);
        }
      },
    }
  );

  const addProjectToCache = useCallback(
    (createdProject) => {
      if (!createdProject) return;
      queryClient.setQueryData("projects", (prev) => {
        const normalized = {
          ...createdProject,
          value: createdProject.id,
          label: createdProject.name,
        };
        if (!prev) return [normalized];
        if (Array.isArray(prev)) return [...prev, normalized];
        if (Array.isArray(prev?.results)) {
          return { ...prev, results: [...prev.results, normalized] };
        }
        if (Array.isArray(prev?.data)) {
          return { ...prev, data: [...prev.data, normalized] };
        }
        return prev;
      });
    },
    [queryClient]
  );

  const { mutateAsync: createProjectQuick, isLoading: isCreatingProject } = useMutation(
    "createProjectQuick",
    createProject,
    {
      onSuccess: (createdProject) => {
        addProjectToCache(createdProject);
        if (createdProject?.id) {
          setSelectedProjectIdForArea(createdProject.id);
        }
      },
    }
  );

  // Filter trees by selected project
  const trees = useMemo(
    () => filterTreesByProject(allCustomerTrees, selectedProjectId),
    [allCustomerTrees, selectedProjectId]
  );

  // Group trees by area for list rendering
  const groupedByArea = useMemo(() => {
    const groups = {};
    (trees || []).forEach((tree) => {
      const areaName = tree.area_name || t("other");
      if (!groups[areaName]) groups[areaName] = [];
      groups[areaName].push(tree);
    });
    return Object.entries(groups).sort(([a], [b]) => (a || "").localeCompare(b || ""));
  }, [trees, t]);

  // Normalize positions once for reuse
  const treesWithPosition = useMemo(
    () =>
      trees
        .map((tree) => {
          const latlng = toLatLng(tree);
          return { ...tree, latlng };
        })
        .filter((t) => t.latlng),
    [trees]
  );

  const selectedTreeFromUrl = tidNumber ? treesWithPosition.find((t) => t.id === tidNumber) : null;

  const { data: selectedTreeDetail } = useQuery(
    ["standaloneMapTree", tidNumber],
    () => getTree(tidNumber),
    {
      // The list serializer omits edit-only fields like phase, so force the detail fetch in edit mode.
      enabled: Boolean(tidNumber && (!selectedTreeFromUrl || isEditMode)),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    }
  );

  // Calculate map center from trees or default
  const selectedTreeDetailWithPosition = useMemo(() => {
    if (!selectedTreeDetail) return null;
    const latlng = toLatLng(selectedTreeDetail);
    return latlng ? { ...selectedTreeDetail, latlng } : selectedTreeDetail;
  }, [selectedTreeDetail]);

  // Calculate centroid from trees
  const calculateCentroid = useCallback((trees) => {
    if (!trees || trees.length === 0) return null;

    const validTrees = trees.filter((tree) => tree.latlng);
    if (validTrees.length === 0) return null;

    const sum = validTrees.reduce(
      (acc, tree) => ({
        lat: acc.lat + tree.latlng.lat,
        lng: acc.lng + tree.latlng.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / validTrees.length,
      lng: sum.lng / validTrees.length,
    };
  }, []);

  const mapCenter = selectedTreeFromUrl?.latlng ||
    selectedTreeDetailWithPosition?.latlng ||
    calculateCentroid(treesWithPosition) || [48.1558269, 11.5421791];

  // Seed edit position from the best available source (selected tree, any tree, or map center) once.
  useEffect(() => {
    if (!isEditMode) return;
    if (editPosition) return;

    // Editing should start from the selected tree's persisted position only.
    // Do not fall back to another tree or the current map center, otherwise the edit flow
    // incorrectly enters the position-picking state before the actual tree detail is ready.
    const fallbackLatLng = selectedTreeFromUrl?.latlng || selectedTreeDetailWithPosition?.latlng;

    if (!fallbackLatLng) return;
    ignoreNextFormSyncRef.current = true;
    const lat = Array.isArray(fallbackLatLng) ? fallbackLatLng[0] : fallbackLatLng.lat;
    const lng = Array.isArray(fallbackLatLng) ? fallbackLatLng[1] : fallbackLatLng.lng;
    setEditPosition({ lat, lng });
    setIsEditPositionConfirmed(true);
  }, [
    isEditMode,
    editPosition,
    selectedTreeFromUrl,
    selectedTreeDetailWithPosition,
    treesWithPosition,
    mapCenter,
  ]);

  const flyToTree = useCallback((tree) => {
    if (!tree) return;
    const latlng = toLatLng(tree);
    const leafletMap = mapRef.current;

    if (leafletMap && latlng) {
      leafletMap.flyTo(latlng, Math.max(leafletMap.getZoom(), 18));
    } else if (!leafletMap && tree?.id) {
      setPendingFlyTreeId(tree.id);
    }
  }, []);

  // If we tried to fly before the map existed, complete it once the map is ready
  useEffect(() => {
    if (mapReady && pendingFlyTreeId) {
      const targetTree = treesWithPosition.find((t) => t.id === pendingFlyTreeId);
      if (targetTree) {
        flyToTree(targetTree);
        setPendingFlyTreeId(null);
      }
    }
  }, [mapReady, pendingFlyTreeId, treesWithPosition, flyToTree]);

  useEffect(() => {
    if (isAddingTree || isEditMode) return;
    const targetId = openTreePopup || tidNumber;
    if (mapReady && targetId) {
      const targetTree = treesWithPosition.find((t) => t.id === targetId);
      flyToTree(targetTree);
    }
  }, [openTreePopup, tidNumber, mapReady, treesWithPosition, flyToTree, isAddingTree, isEditMode]);

  useEffect(() => {
    if (!openTreePopup || isAddingTree || isEditMode) return;
    const params = new URLSearchParams(location.search);
    const nextSelectedTree = String(openTreePopup);
    if (params.get("selectedTree") === nextSelectedTree) return;
    params.set("selectedTree", nextSelectedTree);
    navigate(`${getMapUrl()}${params.toString() ? `?${params.toString()}` : ""}`, {
      replace: true,
    });
  }, [openTreePopup, isAddingTree, isEditMode, location.search, navigate, getMapUrl]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    mapRef.current.invalidateSize();
  }, [mapReady]);

  const handleMapReady = useCallback(() => {
    setMapReady(true);
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 0);
  }, []);

  const detailPanelOpen =
    (isAddingTree && isPositionConfirmed) ||
    (isEditMode && isEditPositionConfirmed) ||
    (!useMapDetailDrawer && (openTreePopup || tidNumber));
  const isPickingPosition = isAddingTree && !isPositionConfirmed;
  const isPickingEditPosition = isEditMode && !isEditPositionConfirmed && Boolean(editPosition);
  console.log("[DEBUG isPickingEditPosition]", {
    isEditMode,
    isEditPositionConfirmed,
    hasEditPosition: Boolean(editPosition),
    result: isPickingEditPosition,
  });
  const baseMapColLg = isPickingEditPosition
    ? 12
    : openList
    ? detailPanelOpen
      ? 4
      : 9
    : detailPanelOpen
    ? 7
    : 12;
  const activeTreeId = isAddingTree ? null : openTreePopup || tidNumber;
  const editingTreeId = isEditMode ? activeTreeId : null;
  const activeTree = activeTreeId
    ? isEditMode
      ? Number(selectedTreeDetailWithPosition?.id) === Number(activeTreeId)
        ? selectedTreeDetailWithPosition
        : null
      : Number(selectedTreeDetailWithPosition?.id) === Number(activeTreeId)
      ? selectedTreeDetailWithPosition
      : trees.find((t) => t.id === activeTreeId)
    : null;

  // Normalize position to {lat, lng} object for form compatibility — the list API returns
  // coordinates in a different format (e.g. tree[0]/tree[1]) but the edit form expects position.lat/lng.
  const activeTreeForForm = useMemo(() => {
    if (!activeTree) return null;
    const latlng = toLatLng(activeTree);
    if (!latlng) return activeTree;

    // If editing and position was confirmed, use editPosition instead of tree's original position
    const positionToUse =
      isEditMode && isEditPositionConfirmed && editPosition
        ? { lat: editPosition.lat, lng: editPosition.lng, alt: activeTree.position?.alt ?? null }
        : { lat: latlng.lat, lng: latlng.lng, alt: activeTree.position?.alt ?? null };

    return {
      ...activeTree,
      position: positionToUse,
    };
  }, [activeTree, isEditMode, isEditPositionConfirmed, editPosition]);
  const treesForMapMarkers = useMemo(() => {
    if (!editingTreeId) return treesWithPosition;
    return treesWithPosition.filter((tree) => Number(tree.id) !== Number(editingTreeId));
  }, [treesWithPosition, editingTreeId]);
  const treeHealthLayerOptions = useMemo(
    () =>
      mapTreeStatusOptions.map((option) => {
        const matchingTrees = treesForMapMarkers.filter(option.match);
        return {
          ...option,
          trees: matchingTrees,
          label: t(option.labelKey),
          name: `${option.iconLabel} ${t(option.labelKey)} (${matchingTrees.length})`,
        };
      }),
    [t, treesForMapMarkers]
  );

  const sensorFilterOptions = useMemo(() => {
    const withSensorTrees = treesForMapMarkers.filter((tree) => tree.has_active_sensor);
    const withoutSensorTrees = treesForMapMarkers.filter((tree) => !tree.has_active_sensor);
    return [
      {
        key: "withSensor",
        labelKey: "active_installation",
        name: `📡 ${t("active_installation")} (${withSensorTrees.length})`,
        match: (tree) => tree.has_active_sensor,
      },
      {
        key: "withoutSensor",
        labelKey: "no_installation",
        name: `⚫ ${t("no_installation")} (${withoutSensorTrees.length})`,
        match: (tree) => !tree.has_active_sensor,
      },
    ];
  }, [t, treesForMapMarkers]);

  const uniqueAreas = useMemo(() => {
    const areaMap = new Map();
    treesWithPosition.forEach((tree) => {
      const areaName = tree.area_name || t("other");
      if (!areaMap.has(areaName)) {
        areaMap.set(areaName, 0);
      }
      areaMap.set(areaName, areaMap.get(areaName) + 1);
    });
    return Array.from(areaMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [treesWithPosition, t]);
  const filteredTreesForMapMarkers = useMemo(() => {
    return treesForMapMarkers.filter((tree) => {
      const healthMatch = treeHealthLayerOptions.some(
        (option) => activeTreeHealthFilters[option.key] && option.match(tree)
      );
      if (!healthMatch) return false;

      const sensorMatch =
        (activeSensorFilters.withSensor && tree.has_active_sensor) ||
        (activeSensorFilters.withoutSensor && !tree.has_active_sensor);
      if (!sensorMatch) return false;

      const selectedAreas = Object.keys(activeAreaFilters).filter((key) => activeAreaFilters[key]);
      if (selectedAreas.length > 0) {
        const treeAreaName = tree.area_name || t("other");
        if (!activeAreaFilters[treeAreaName]) return false;
      }

      return true;
    });
  }, [
    activeTreeHealthFilters,
    activeSensorFilters,
    activeAreaFilters,
    treeHealthLayerOptions,
    treesForMapMarkers,
    t,
  ]);
  const isTreeDetailMode = !isAddingTree && !editingTreeId && Boolean(activeTreeId);
  const isEditFocusMode =
    isTreeDetailMode &&
    isSensorEditOpen &&
    !openList &&
    !isPickingEditPosition &&
    !isAddingTree &&
    !isEditMode;
  const mapColLg =
    isEditFocusMode || (isTreeDetailMode && !openList && !isPickingEditPosition) ? 6 : baseMapColLg;
  const detailTreeColLg = openList ? 5 : 6;
  const pidForDetail =
    selectedProjectId ?? activeTree?.project_id ?? activeTree?.area?.project?.id ?? null;
  const aidForDetail = activeTree?.area_id ?? activeTree?.area?.id ?? null;

  const closeMapTreeDrawer = useCallback(() => {
    setOpenTreePopup(null);
    const params = new URLSearchParams(location.search);
    params.delete("selectedTree");
    navigate(`${getMapUrl()}${params.toString() ? `?${params.toString()}` : ""}`, {
      replace: true,
    });
  }, [location.search, navigate, getMapUrl]);

  useEffect(() => {
    const shouldFocusAddDetail = isAddingTree && isPositionConfirmed;
    const shouldFocusEditDetail = Boolean(editingTreeId) && isEditPositionConfirmed;

    if (!shouldFocusAddDetail && !shouldFocusEditDetail) return;
    if (typeof window === "undefined" || window.innerWidth >= 992) return;

    const target = shouldFocusAddDetail ? addDetailPanelRef.current : editDetailPanelRef.current;
    if (!target) return;

    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => cancelAnimationFrame(raf);
  }, [isAddingTree, isPositionConfirmed, editingTreeId, isEditPositionConfirmed]);

  useEffect(() => {
    if (!detailPanelOpen || isAddingTree || isEditMode) {
      setIsSensorEditOpen(false);
    }
  }, [detailPanelOpen, isAddingTree, isEditMode]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    let cancelled = false;
    let rafId = null;
    const safeInvalidateSize = () => {
      if (cancelled) return;
      const currentMap = mapRef.current;
      if (!currentMap || currentMap !== m) return;
      if (!currentMap._container || !currentMap._mapPane || !currentMap._loaded) return;
      try {
        currentMap.invalidateSize();
      } catch (err) {
        console.warn("Skipping map.invalidateSize on stale map instance", err);
      }
    };

    rafId = requestAnimationFrame(safeInvalidateSize);
    const timer = setTimeout(safeInvalidateSize, 150);
    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [openList, openTreePopup, tid, isSensorEditOpen, mapColLg]);

  const startAddTree = useCallback(() => {
    previousSelectionRef.current = { openTreePopup, selectedTree };
    setOpenTreePopup(null);
    setSelectedTree(null);
    setIsAddingTree(true);
    setIsPositionConfirmed(false);
    if (mapRef.current) {
      ignoreNextFormSyncRef.current = true;
      setAddPosition(mapRef.current.getCenter());
    } else {
      setAddPosition(null);
    }
  }, [openTreePopup, selectedTree]);

  const shouldAutoAddTree = query.get("addTree") === "1" || query.get("addTree") === "true";

  useEffect(() => {
    if (!shouldAutoAddTree || isAddingTree) return;
    startAddTree();
  }, [shouldAutoAddTree, isAddingTree, startAddTree]);

  const cancelAddTree = useCallback(() => {
    // Clear auto-add query flag so positioning doesn't immediately reopen after cancel.
    const params = new URLSearchParams(location.search);
    params.delete("addTree");
    navigate(`${getMapUrl()}${params.toString() ? `?${params.toString()}` : ""}`);

    const previous = previousSelectionRef.current;
    setIsAddingTree(false);
    setAddPosition(null);
    setIsPositionConfirmed(false);
    setOpenTreePopup(previous.openTreePopup || null);
    setSelectedTree(previous.selectedTree || null);
    setSelectedAreaId(null);
    setSelectedProjectIdForArea(null);
  }, [location.search, navigate, getMapUrl]);

  const handleFormPositionChange = useCallback(
    (latlng) => {
      if (!latlng) return;
      if (ignoreNextFormSyncRef.current) {
        ignoreNextFormSyncRef.current = false;
        return;
      }
      if (isSameLatLng(addPosition, latlng)) return;
      setAddPosition(latlng);
      if (mapRef.current) {
        mapRef.current.setView(latlng, mapRef.current.getZoom());
      }
    },
    [addPosition]
  );

  const handleEditPositionChange = useCallback((latlng) => {
    if (!latlng) return;
    if (ignoreNextFormSyncRef.current) {
      ignoreNextFormSyncRef.current = false;
      return;
    }
    // During picking phase (not confirmed), ignore updates from PositionReporter
    // to prevent form's stale values from resetting the map-dragged position
    if (!isEditPositionConfirmedRef.current) return;
    if (isSameLatLng(editPositionRef.current, latlng)) return;
    setEditPosition(latlng);
  }, []);

  const handleEditPositionConfirm = useCallback(() => {
    setIsEditPositionConfirmed(true);
  }, []);

  const cancelEditTree = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("editTree");
    navigate(`${getMapUrl()}?${params.toString()}`);
  }, [location.search, navigate, getMapUrl]);

  useEffect(() => {
    if (!isEditMode) return;
    if (!editPosition || !mapRef.current) return;
    if (isEditPositionConfirmed) return;
    if (editFlyRef.current) return;
    editFlyRef.current = true;
    mapRef.current.flyTo(editPosition, Math.max(mapRef.current.getZoom(), 18));
  }, [isEditMode, editPosition, isEditPositionConfirmed]);

  useEffect(() => {
    if (!editingTreeId) {
      setEditPosition(null);
      setIsEditPositionConfirmed(false);
      editFlyRef.current = false;
      return;
    }
    const tree = activeTree;
    const inferredProjectId = tree?.project_id || tree?.area?.project?.id || null;
    if (!selectedProjectId && inferredProjectId) {
      setSelectedProjectIdForArea(inferredProjectId);
    }
    const inferredAreaId = tree?.area_id || tree?.area?.id || null;
    if (inferredAreaId) {
      setSelectedAreaId(inferredAreaId);
    }
    if (editPosition) {
      return;
    }
    const latlng = toLatLng(tree);
    if (!latlng) {
      return;
    }

    ignoreNextFormSyncRef.current = true;
    setEditPosition({ lat: latlng.lat, lng: latlng.lng });
    setIsEditPositionConfirmed(true);
  }, [editingTreeId, activeTree, selectedProjectId, editPosition]);

  useEffect(() => {
    if (!isAddingTree) return;
    if (selectedAreaId) return;
    if (areaIdParam) {
      setSelectedAreaId(Number(areaIdParam));
      return;
    }
    if (availableAreas.length === 1) {
      setSelectedAreaId(availableAreas[0].id);
    }
  }, [isAddingTree, selectedAreaId, areaIdParam, availableAreas]);

  useEffect(() => {
    if (!selectedProjectId) return;
    setSelectedProjectIdForArea(null);
  }, [selectedProjectId]);

  useEffect(() => {
    if (!effectiveProjectId) return;
    if (!allAreas.length) return;
    if (isEditMode) return;
    if (selectedAreaId && availableAreas.some((area) => area.id === selectedAreaId)) {
      return;
    }
    setSelectedAreaId(null);
  }, [effectiveProjectId, availableAreas, selectedAreaId, allAreas.length, isEditMode]);

  const handleMapCenterChange = useCallback(
    (latlng) => {
      if (!latlng) return;
      if (isPositionConfirmed) return;
      if (isSameLatLng(addPosition, latlng)) return;

      ignoreNextFormSyncRef.current = true;
      setAddPosition(latlng);
    },
    [addPosition, isPositionConfirmed]
  );

  const handleEditMapCenterChange = useCallback((latlng) => {
    if (!latlng) {
      return;
    }
    if (isEditPositionConfirmedRef.current) {
      return;
    }
    if (ignoreNextFormSyncRef.current) {
      ignoreNextFormSyncRef.current = false;
      return;
    }
    // Ignore bogus (0,0) centers to prevent oscillation when map hasn't settled
    if (editPositionRef.current && Math.abs(latlng.lat) < 1e-6 && Math.abs(latlng.lng) < 1e-6) {
      return;
    }
    const prev = editPositionRef.current;
    if (isSameLatLng(prev, latlng)) {
      return;
    }
    ignoreNextFormSyncRef.current = true;
    setEditPosition(latlng);
  }, []);

  const addFormInitialValues = useMemo(() => {
    if (!addPosition) return null;
    return {
      ...formInitialValues,
      soil: { ...formInitialValues.soil },
      latitude: addPosition.lat,
      longitude: addPosition.lng,
    };
  }, [addPosition]);

  const handleTreeCreated = useCallback(
    (createdTree) => {
      setIsAddingTree(false);
      setAddPosition(null);
      setIsPositionConfirmed(false);
      const createdTreeId = createdTree?.id ? String(createdTree.id) : null;
      const params = new URLSearchParams(location.search);
      const origin = params.get("origin");
      params.delete("addTree");
      params.delete("editTree");
      params.delete("origin");
      if (createdTreeId) {
        params.set("selectedTree", createdTreeId);
      }

      if (createdTree?.id) {
        setOpenTreePopup(createdTree.id);
        setSelectedTree(createdTree);
        if (mapRef.current && createdTree.position) {
          mapRef.current.flyTo(createdTree.position, Math.max(mapRef.current.getZoom(), 18));
        }
      }

      if (origin === "trees" && createdTreeId) {
        navigate(`/trees?selectedTree=${createdTreeId}`);
        return;
      }

      navigate(`${getMapUrl()}${params.toString() ? `?${params.toString()}` : ""}`, {
        replace: true,
      });
    },
    [location.search, navigate, setOpenTreePopup, setSelectedTree, getMapUrl]
  );

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const container = m.getContainer();
    container.style.cursor = isPickingPosition ? "grab" : "";
    return () => {
      container.style.cursor = "";
    };
  }, [isPickingPosition, mapReady]);

  useEffect(() => {
    if (!isAddingTree || addPosition || !mapReady || !mapRef.current) return;
    ignoreNextFormSyncRef.current = true;
    setAddPosition(mapRef.current.getCenter());
  }, [isAddingTree, addPosition, mapReady]);

  useEffect(() => {
    isEditPositionConfirmedRef.current = isEditPositionConfirmed;
  }, [isEditPositionConfirmed]);

  useEffect(() => {
    editPositionRef.current = editPosition;
  }, [editPosition]);

  useEffect(() => {
    if (!selectedProjectId) return;

    if (openTreePopup && !trees.some((t) => t?.id === openTreePopup)) {
      setOpenTreePopup(null);
      setSelectedTree(null);
    }
  }, [selectedProjectId, trees, openTreePopup]);

  // Close area filter dropdown when clicking outside
  useEffect(() => {
    if (!areaFilterOpen) return;

    const handleClickOutside = (event) => {
      if (areaFilterRef.current && !areaFilterRef.current.contains(event.target)) {
        setAreaFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [areaFilterOpen]);

  // Initialize area filters - select all areas by default
  useEffect(() => {
    if (uniqueAreas.length === 0) return;

    setActiveAreaFilters((prev) => {
      const updated = { ...prev };
      uniqueAreas.forEach((area) => {
        // Only set to true if not already present (preserve user selection)
        if (!(area.name in updated)) {
          updated[area.name] = true;
        }
      });
      return updated;
    });
  }, [uniqueAreas]);

  // Style the sensor filter header
  useEffect(() => {
    if (!mapReady) return;

    const styleSensorHeader = () => {
      const labels = document.querySelectorAll(".leaflet-control-layers-overlays label");
      labels.forEach((label) => {
        const span = label.querySelector("span");
        if (span && span.textContent.includes("╶╴")) {
          label.classList.add("sensor-filter-header");
        }
      });
    };

    // Run immediately and after a short delay to catch late renders
    styleSensorHeader();
    const timer = setTimeout(styleSensorHeader, 100);

    return () => clearTimeout(timer);
  }, [mapReady]);

  // Fly to centroid when project filter changes (initial load or filter change)
  const prevProjectIdRef = useRef(selectedProjectId);
  const hasInitialFlyRef = useRef(false);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (isAddingTree || isEditMode) return;
    if (tidNumber || openTreePopup) return; // Don't fly if we have a specific tree selected

    // Fly on initial load with project filter, or when project ID changes
    const projectChanged = prevProjectIdRef.current !== selectedProjectId;
    const isInitialLoad = !hasInitialFlyRef.current && selectedProjectId;

    prevProjectIdRef.current = selectedProjectId;

    if (!projectChanged && !isInitialLoad) return;

    const centroid = calculateCentroid(treesWithPosition);
    if (centroid && treesWithPosition.length > 0) {
      hasInitialFlyRef.current = true;
      mapRef.current.flyTo(centroid, 13, { duration: isInitialLoad ? 0 : 0.8 });
    }
  }, [
    selectedProjectId,
    mapReady,
    calculateCentroid,
    treesWithPosition,
    isAddingTree,
    isEditMode,
    tidNumber,
    openTreePopup,
  ]);

  // Fly to centroid when area filter changes
  const prevAreaFiltersRef = useRef(activeAreaFilters);
  const areaFilterInitializedRef = useRef(false);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (isAddingTree || isEditMode) return;

    const selectedAreas = Object.keys(activeAreaFilters).filter((key) => activeAreaFilters[key]);
    const prevSelectedAreas = Object.keys(prevAreaFiltersRef.current).filter(
      (key) => prevAreaFiltersRef.current[key]
    );

    // Check if filters actually changed
    const filtersChanged =
      selectedAreas.length !== prevSelectedAreas.length ||
      selectedAreas.some((area) => !prevSelectedAreas.includes(area));

    prevAreaFiltersRef.current = activeAreaFilters;

    // Skip on initial load when all areas are selected
    if (!areaFilterInitializedRef.current) {
      areaFilterInitializedRef.current = true;
      return;
    }

    if (!filtersChanged || selectedAreas.length === 0) return;

    const filteredByArea = treesWithPosition.filter((tree) => {
      const treeAreaName = tree.area_name || t("other");
      return activeAreaFilters[treeAreaName];
    });

    const centroid = calculateCentroid(filteredByArea);
    if (centroid && filteredByArea.length > 0) {
      mapRef.current.flyTo(centroid, Math.max(mapRef.current.getZoom(), 15), { duration: 0.8 });
    }
  }, [
    activeAreaFilters,
    mapReady,
    calculateCentroid,
    treesWithPosition,
    t,
    isAddingTree,
    isEditMode,
  ]);

  function AreaProjectPicker({
    projects,
    onProjectSelect,
    onCreateProject,
    isCreatingProject,
    disabledCreateProject,
    areas,
    onAreaSelect,
    onCreateArea,
    isCreatingArea,
    disabledCreateArea,
    selectedProjectId,
    selectedAreaId,
    projectLocked = false,
    disabled = false,
  }) {
    const [projectInput, setProjectInput] = useState("");
    const [areaInput, setAreaInput] = useState("");
    const [projectOpen, setProjectOpen] = useState(false);
    const [areaOpen, setAreaOpen] = useState(false);

    useEffect(() => {
      if (!selectedProjectId) {
        setProjectInput("");
        return;
      }
      const project = projects.find((p) => p.id === selectedProjectId);
      if (project?.name) {
        setProjectInput(project.name);
      }
    }, [selectedProjectId, projects]);

    useEffect(() => {
      if (!selectedAreaId) {
        setAreaInput("");
        return;
      }
      const area = areas.find((a) => a.id === selectedAreaId);
      if (area?.name) {
        setAreaInput(area.name);
      }
    }, [selectedAreaId, areas]);

    const normalizedProjectInput = projectInput.trim().toLowerCase();
    const normalizedAreaInput = areaInput.trim().toLowerCase();
    const projectOptions = projects.filter((p) =>
      normalizedProjectInput ? (p.name || "").toLowerCase().includes(normalizedProjectInput) : true
    );
    const areaOptions = areas.filter((a) =>
      normalizedAreaInput ? (a.name || "").toLowerCase().includes(normalizedAreaInput) : true
    );
    const hasExactProjectMatch =
      normalizedProjectInput &&
      projects.some((p) => (p.name || "").toLowerCase() === normalizedProjectInput);
    const hasExactAreaMatch =
      normalizedAreaInput &&
      areas.some((a) => (a.name || "").toLowerCase() === normalizedAreaInput);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div className="map-form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
          {t("project.name")}
        </div>
        {selectedProjectId ? (
          <div
            className="map-form-badge map-form-badge-project"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 10px",
              borderRadius: "999px",
              border: "1px solid #e5e7eb",
              fontSize: "0.875rem",
              fontWeight: 600,
              maxWidth: "100%",
            }}
          >
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {projectInput}
            </span>
            {!disabled && !projectLocked && (
              <button
                type="button"
                onClick={() => onProjectSelect({ id: null })}
                aria-label={t("remove")}
                className="map-form-badge-remove"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "14px",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              <Form.Control
                size="sm"
                placeholder={t("name")}
                value={projectInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setProjectInput(value);
                }}
                onFocus={() => setProjectOpen(true)}
                onBlur={() => setProjectOpen(false)}
                disabled={disabled}
                className="map-form-input"
                style={{
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  marginBottom: 0,
                }}
              />
              {projectOpen && (
                <div
                  className="map-form-dropdown"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderTop: "none",
                    borderRadius: "0 0 10px 10px",
                    maxHeight: 180,
                    overflowY: "auto",
                    padding: "4px 0",
                    marginTop: "0",
                  }}
                >
                  {projectInput.trim().length > 0 && !hasExactProjectMatch && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onCreateProject(projectInput.trim())}
                      disabled={disabledCreateProject || isCreatingProject || disabled}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "8px 10px",
                        borderRadius: "0",
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#0d6efd",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      {t("newproject")} “{projectInput.trim()}”
                    </button>
                  )}
                  {projectOptions.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onProjectSelect(project)}
                      disabled={disabled}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "8px 10px",
                        borderRadius: "0",
                        cursor: "pointer",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      {project.name || project.title || `#${project.id}`}
                    </button>
                  ))}
                  {projectOptions.length === 0 && (
                    <div style={{ padding: "8px 10px", color: "#6b7280", fontSize: "12px" }}>
                      {t("health_states.no_data")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        <div className="map-form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
          {t("area")}
        </div>
        {selectedAreaId ? (
          <div
            className="map-form-badge map-form-badge-area"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 10px",
              borderRadius: "999px",
              border: "1px solid #e5e7eb",
              fontSize: "0.875rem",
              fontWeight: 600,
              maxWidth: "100%",
            }}
          >
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {areaInput}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => onAreaSelect({ id: null })}
                aria-label={t("remove")}
                className="map-form-badge-remove"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "14px",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              <Form.Control
                size="sm"
                placeholder={t("name")}
                value={areaInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setAreaInput(value);
                }}
                onFocus={() => setAreaOpen(true)}
                onBlur={() => setAreaOpen(false)}
                disabled={disabled || !selectedProjectId}
                className="map-form-input"
                style={{
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  marginBottom: 0,
                }}
              />
              {areaOpen && (
                <div
                  className="map-form-dropdown"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderTop: "none",
                    borderRadius: "0 0 10px 10px",
                    maxHeight: 180,
                    overflowY: "auto",
                    padding: "4px 0",
                    marginTop: "0",
                    opacity: !selectedProjectId ? 0.6 : 1,
                    pointerEvents: !selectedProjectId ? "none" : "auto",
                  }}
                >
                  {areaInput.trim().length > 0 && !hasExactAreaMatch && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onCreateArea(areaInput.trim())}
                      disabled={disabledCreateArea || isCreatingArea || disabled}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "8px 10px",
                        borderRadius: "0",
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#0d6efd",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      {t("add_area")} “{areaInput.trim()}”
                    </button>
                  )}
                  {areaOptions.map((area) => (
                    <button
                      key={area.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onAreaSelect(area)}
                      disabled={disabled}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "8px 10px",
                        borderRadius: "0",
                        cursor: "pointer",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      {area.name || area.title || `#${area.id}`}
                    </button>
                  ))}
                  {areaOptions.length === 0 && (
                    <div style={{ padding: "8px 10px", color: "#6b7280", fontSize: "12px" }}>
                      {t("health_states.no_data")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  function PositionInputPanel({
    position,
    onChange,
    onConfirm,
    onCancel,
    onUseMyLocation,
    disabledConfirm,
    locked = false,
    mapRef,
  }) {
    const [lat, setLat] = useState(position?.lat ?? "");
    const [lng, setLng] = useState(position?.lng ?? "");
    const latRef = useRef(position?.lat ?? "");
    const lngRef = useRef(position?.lng ?? "");

    useEffect(() => {
      if (position?.lat == null || position?.lng == null) {
        return;
      }
      const nextLat = position.lat.toFixed(6);
      const nextLng = position.lng.toFixed(6);
      latRef.current = nextLat;
      lngRef.current = nextLng;
      setLat(nextLat);
      setLng(nextLng);
    }, [position?.lat, position?.lng]);

    const commit = () => {
      const nLat = Number(latRef.current);
      const nLng = Number(lngRef.current);
      if (
        Number.isFinite(nLat) &&
        Number.isFinite(nLng) &&
        nLat >= -90 &&
        nLat <= 90 &&
        nLng >= -180 &&
        nLng <= 180
      ) {
        const nextPosition = { lat: nLat, lng: nLng };
        onChange(nextPosition);
        return nextPosition;
      }
      return null;
    };
    const isNarrowViewport = typeof window !== "undefined" && window.innerWidth <= 768;

    return (
      <div
        style={{
          position: "absolute",
          top: isNarrowViewport ? "8px" : "12px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: "var(--color-bg-surface, #ffffff)",
          border: "1px solid var(--color-border-subtle, #d8e4ea)",
          borderRadius: "10px",
          padding: isNarrowViewport ? "0.625rem" : "0.75rem",
          boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          width: isNarrowViewport ? "calc(100% - 16px)" : "auto",
          minWidth: isNarrowViewport ? 0 : 360,
          maxWidth: isNarrowViewport ? "calc(100% - 16px)" : "min(92vw, 560px)",
        }}
      >
        {/* Title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 700,
            fontSize: "0.875rem",
            color: "var(--color-text-primary, #0e1d2c)",
          }}
        >
          {locked ? t("position_confirmed") : t("map_add_tree_mode")}
          {!locked && (
            <Button
              size="sm"
              onClick={onUseMyLocation}
              title={t("use_my_location")}
              style={{
                ...mapSecondaryButtonStyle,
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              <CrosshairIcon style={{ color: "var(--color-text-primary, #0e1d2c)" }} />
            </Button>
          )}
        </div>

        {/* Address search */}
        {!locked && (
          <AddressSearchInput
            onSelect={(latlng) => {
              onChange(latlng);
              if (mapRef?.current) {
                mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 18));
              }
            }}
            placeholder={t("search_address")}
            disabled={locked}
            mapRef={mapRef}
          />
        )}

        {/* Inputs + actions */}
        <div
          style={{
            display: "flex",
            alignItems: isNarrowViewport ? "stretch" : "center",
            flexDirection: isNarrowViewport ? "column" : "row",
            gap: "0.5rem",
          }}
        >
          <div
            style={{ display: "flex", gap: "0.5rem", width: isNarrowViewport ? "100%" : "auto" }}
          >
            <input
              type="number"
              disabled={locked}
              step="0.000001"
              value={lat}
              onChange={(e) => {
                latRef.current = e.target.value;
                setLat(e.target.value);
              }}
              placeholder="Lat"
              style={{
                ...mapOverlayInputStyle,
                width: isNarrowViewport ? "50%" : 120,
                minWidth: 0,
              }}
            />
            <input
              type="number"
              disabled={locked}
              step="0.000001"
              value={lng}
              onChange={(e) => {
                lngRef.current = e.target.value;
                setLng(e.target.value);
              }}
              placeholder="Lng"
              style={{
                ...mapOverlayInputStyle,
                width: isNarrowViewport ? "50%" : 120,
                minWidth: 0,
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              marginLeft: isNarrowViewport ? 0 : "0.5rem",
              justifyContent: isNarrowViewport ? "flex-end" : "flex-start",
              width: isNarrowViewport ? "100%" : "auto",
            }}
          >
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                const nextPosition = commit();
                onConfirm(nextPosition);
              }}
              disabled={disabledConfirm}
              style={mapPrimaryButtonStyle}
            >
              {t("confirm")}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={onCancel}
              style={mapSecondaryButtonStyle}
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latlng = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };

        ignoreNextFormSyncRef.current = true;
        setAddPosition(latlng);

        if (mapRef.current) {
          mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 18));
        }
      },
      (err) => {
        console.warn("Geolocation error:", err);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  const areaProjectPickerProps = {
    projects: allProjects,
    onProjectSelect: (project) => {
      setSelectedProjectIdForArea(project?.id || null);
      if (!project?.id) {
        setSelectedAreaId(null);
      }
    },
    onCreateProject: (name) => {
      if (!name) return;
      createProjectQuick({ name });
    },
    isCreatingProject,
    disabledCreateProject: false,
    areas: availableAreas,
    onAreaSelect: (area) => {
      setSelectedAreaId(area?.id || null);
    },
    onCreateArea: (name) => {
      if (!name) return;
      if (!effectiveProjectId) return;
      const payload = {
        name,
        project: { type: "Project", id: effectiveProjectId },
      };
      createArea(payload);
    },
    isCreatingArea,
    disabledCreateArea: !effectiveProjectId,
    selectedProjectId: selectedProjectId ?? selectedProjectIdForArea,
    selectedAreaId,
    projectLocked: Boolean(selectedProjectId),
  };

  function AreaProjectPickerField({ values, setFieldValue }) {
    useEffect(() => {
      if (selectedAreaId && values.area !== selectedAreaId) {
        setFieldValue("area", selectedAreaId);
      }
    }, [selectedAreaId, values.area, setFieldValue]);

    const handleAreaSelect = (area) => {
      const nextId = area?.id || null;
      setSelectedAreaId(nextId);
      setFieldValue("area", nextId || "");
    };

    const handleProjectSelect = (project) => {
      const nextId = project?.id || null;
      setSelectedProjectIdForArea(nextId);
      if (!nextId) {
        setSelectedAreaId(null);
        setFieldValue("area", "");
      }
    };

    return (
      <AreaProjectPicker
        {...areaProjectPickerProps}
        selectedAreaId={values.area || selectedAreaId}
        onAreaSelect={handleAreaSelect}
        onProjectSelect={handleProjectSelect}
      />
    );
  }

  return (
    <>
      {isLoading ? (
        <Spinner height="80vh" />
      ) : (
        <div
          data-tour="tree-map"
          style={{
            position: "fixed",
            top: `calc(${topOffset} + 8px)`,
            left: "8px",
            right: "8px",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
            overflowX: "hidden",
            overflowY: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Container
            fluid
            style={{
              height: "100%",
              maxWidth: "100%",
              paddingBottom: 0,
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <Row className="g-3 entity-split-row" style={{ height: "100%" }}>
              {openList && !isPickingEditPosition && (
                <Col
                  lg="3"
                  className="entity-master-column"
                  style={{ height: "100%", minHeight: 0 }}
                >
                  <div
                    style={{
                      ...mapPanelStyle,
                      height: "100%",
                      padding: "1rem",
                      overflowY: "auto",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            color: "var(--color-text-primary, #0e1d2c)",
                            fontSize: "14px",
                          }}
                        >
                          {t("trees")}
                        </div>
                        <small className="text-muted">
                          {trees.length} {t("trees")}
                        </small>
                      </div>
                      <Button
                        size="sm"
                        variant="light"
                        style={mapSecondaryButtonStyle}
                        onClick={() => {
                          setOpenList(false);
                          setOpenTreePopup(null);
                        }}
                      >
                        <AngleRightIcon />
                      </Button>
                    </div>

                    <div className="d-flex flex-column gap-3" style={{ paddingRight: "0.25rem" }}>
                      {groupedByArea.map(([areaName, areaTrees]) => (
                        <div key={areaName}>
                          <button
                            className="d-flex align-items-center gap-2 w-100"
                            style={{
                              background: "none",
                              border: "none",
                              padding: "0.25rem 0",
                              textAlign: "left",
                              fontWeight: 700,
                              color: "var(--color-text-primary, #0e1d2c)",
                            }}
                            onClick={() =>
                              setGroupedOpen((prev) => ({ ...prev, [areaName]: !prev[areaName] }))
                            }
                          >
                            <TriangleUpIcon open={groupedOpen[areaName] !== false} />
                            <span>{areaName}</span>
                          </button>
                          {groupedOpen[areaName] !== false && (
                            <div className="d-flex flex-column gap-2">
                              {areaTrees
                                .slice()
                                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                .map((tree) => {
                                  const isActive = tree.id === openTreePopup;
                                  return (
                                    <div
                                      key={tree.id}
                                      className="d-flex align-items-center justify-content-between"
                                      style={{
                                        cursor: "pointer",
                                        padding: "0.55rem 0.65rem",
                                        borderRadius: "8px",
                                        background: isActive
                                          ? "var(--color-action-subtle, #e7f1ff)"
                                          : "var(--color-bg-subtle, #f9fafb)",
                                        border: isActive
                                          ? "1px solid var(--color-action, #2569a3)"
                                          : "1px solid var(--color-border-subtle, #e5e7eb)",
                                        transition: "all 0.15s ease",
                                      }}
                                      onClick={() => {
                                        setSelectedTree(tree);
                                        setOpenTreePopup(tree.id);
                                        flyToTree(tree);
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <span
                                          style={{
                                            width: "10px",
                                            height: "10px",
                                            borderRadius: "50%",
                                            background:
                                              tree.health_state === 0
                                                ? "#16a34a"
                                                : tree.health_state === 1
                                                ? "#eab308"
                                                : tree.health_state === 2
                                                ? "#ef4444"
                                                : "#9ca3af",
                                            display: "inline-block",
                                            marginTop: "2px",
                                          }}
                                        />
                                        <div>
                                          <div
                                            style={{
                                              fontWeight: 600,
                                              color: "var(--color-text-primary, #0e1d2c)",
                                            }}
                                          >
                                            {tree.name}
                                          </div>
                                          {tree.species_name_de && (
                                            <small
                                              style={{
                                                color: "var(--color-text-secondary, #5b6b79)",
                                              }}
                                            >
                                              {tree.species_name_de}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                      {tree.species_name && (
                                        <span
                                          style={{
                                            background: "var(--color-action-subtle, #eef2ff)",
                                            color: "var(--color-action, #2569a3)",
                                            borderRadius: "12px",
                                            padding: "2px 8px",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {tree.species_name}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      ))}
                      {groupedByArea.length === 0 && (
                        <div
                          style={{
                            padding: "0.5rem 0",
                            color: "var(--color-text-secondary, #6b7280)",
                            fontSize: "0.875rem",
                          }}
                        >
                          {t("no_trees_found")}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              )}

              <Col
                lg={mapColLg}
                className="entity-map-column"
                style={{
                  height: "100%",
                  position: "relative",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    ...mapCanvasStyle,
                    position: "relative",
                    height: "100%",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      zIndex: 1000,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (openList) setOpenTreePopup(null);
                        setOpenList(!openList);
                      }}
                      style={{
                        ...mapFloatingPrimaryButtonStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <AngleRightIcon
                        style={{ transform: openList ? "rotate(90deg)" : "rotate(0deg)" }}
                      />
                      {openList ? t("close") : t("show_tree_list")}
                    </Button>
                    <div ref={areaFilterRef} style={{ position: "relative" }}>
                      <Button
                        variant="light"
                        onClick={() => setAreaFilterOpen(!areaFilterOpen)}
                        style={{
                          ...mapFloatingPrimaryButtonStyle,
                          background: "var(--color-bg-surface, #ffffff)",
                          border: "1px solid var(--color-border-subtle, #d8e4ea)",
                          color: "var(--color-text-primary, #0e1d2c)",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        📍 {t("filter_by_location")}
                      </Button>
                      {areaFilterOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            background: "var(--color-bg-surface, #ffffff)",
                            border: "1px solid var(--color-border-subtle, #d8e4ea)",
                            borderRadius: "10px",
                            padding: "0.75rem",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                            minWidth: "200px",
                            maxHeight: "300px",
                            overflowY: "auto",
                            zIndex: 1001,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 700,
                              marginBottom: "0.5rem",
                              fontSize: "0.875rem",
                              color: "var(--color-text-primary, #0e1d2c)",
                            }}
                          >
                            {t("filter_by_location")}
                          </div>
                          <div
                            className="area-filter-list"
                            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
                          >
                            {uniqueAreas.map((area) => (
                              <label
                                key={area.name}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  cursor: "pointer",
                                  fontSize: "0.875rem",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="area-filter-checkbox"
                                  checked={Boolean(activeAreaFilters[area.name])}
                                  onChange={(e) => {
                                    setActiveAreaFilters((prev) => ({
                                      ...prev,
                                      [area.name]: e.target.checked,
                                    }));
                                  }}
                                />
                                <span>
                                  {area.name} ({area.count})
                                </span>
                              </label>
                            ))}
                          </div>
                          <div
                            style={{
                              marginTop: "0.75rem",
                              paddingTop: "0.75rem",
                              borderTop: "1px solid var(--color-border-subtle, #e5e7eb)",
                              display: "flex",
                              gap: "0.5rem",
                            }}
                          >
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                const allSelected = {};
                                uniqueAreas.forEach((area) => {
                                  allSelected[area.name] = true;
                                });
                                setActiveAreaFilters(allSelected);
                              }}
                              style={{ flex: 1, fontSize: "0.75rem" }}
                            >
                              {t("select_all")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setActiveAreaFilters({})}
                              style={{ flex: 1, fontSize: "0.75rem" }}
                            >
                              {t("clear")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <MapContainer
                    center={mapCenter}
                    zoom={13}
                    maxZoom={30}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={false}
                  >
                    <MapRefBinder mapRef={mapRef} onReady={handleMapReady} />
                    <ZoomControl position="bottomleft" />
                    <LayersControl position="topright">
                      <LayersControl.BaseLayer checked name="Karte">
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          maxNativeZoom={19}
                          maxZoom={30}
                        />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Satellit">
                        <TileLayer
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                          maxNativeZoom={19}
                          maxZoom={30}
                        />
                      </LayersControl.BaseLayer>
                      {treeHealthLayerOptions.map((option) => (
                        <LayersControl.Overlay
                          key={option.key}
                          checked={Boolean(activeTreeHealthFilters[option.key])}
                          name={option.name}
                        >
                          <LayerGroup
                            eventHandlers={{
                              add: () =>
                                setActiveTreeHealthFilters((prev) => ({
                                  ...prev,
                                  [option.key]: true,
                                })),
                              remove: () =>
                                setActiveTreeHealthFilters((prev) => ({
                                  ...prev,
                                  [option.key]: false,
                                })),
                            }}
                          />
                        </LayersControl.Overlay>
                      ))}
                      <LayersControl.Overlay
                        key="sensor-header"
                        checked={true}
                        name={`╶╴ ${t("active_sensor_installation")}`}
                      >
                        <LayerGroup />
                      </LayersControl.Overlay>
                      {sensorFilterOptions.map((option) => (
                        <LayersControl.Overlay
                          key={option.key}
                          checked={Boolean(activeSensorFilters[option.key])}
                          name={option.name}
                        >
                          <LayerGroup
                            eventHandlers={{
                              add: () =>
                                setActiveSensorFilters((prev) => ({
                                  ...prev,
                                  [option.key]: true,
                                })),
                              remove: () =>
                                setActiveSensorFilters((prev) => ({
                                  ...prev,
                                  [option.key]: false,
                                })),
                            }}
                          />
                        </LayersControl.Overlay>
                      ))}
                    </LayersControl>
                    <TreeClusterLayer
                      trees={filteredTreesForMapMarkers}
                      setOpenTreePopup={setOpenTreePopup}
                      setSelectedTree={setSelectedTree}
                      selectedTree={selectedTree}
                      tid={isAddingTree ? null : tidNumber}
                    />
                    <CenterPositionUpdater
                      enabled={isPickingPosition}
                      onChange={handleMapCenterChange}
                    />
                    <CenterPositionUpdater
                      enabled={isPickingEditPosition}
                      onChange={handleEditMapCenterChange}
                    />
                    {isEditMode && editPosition && activeTree && isEditPositionConfirmed && (
                      <Marker
                        position={editPosition}
                        icon={buildEditIcon(activeTree.has_active_sensor, activeTree.health_state)}
                        interactive={false}
                      />
                    )}
                    {isAddingTree && isPositionConfirmed && addPosition && (
                      <Marker position={addPosition} icon={parentTreeIcon(0)} interactive={false} />
                    )}
                  </MapContainer>
                  {(isPickingPosition || isPickingEditPosition) && (
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: `translate(-50%, -100%) translateY(${centerMarkerTipOffsetY}px)`,
                        pointerEvents: "none",
                        zIndex: 1000,
                        width: 50,
                        height: 50,
                      }}
                      dangerouslySetInnerHTML={{ __html: centerMarkerSvg }}
                    />
                  )}
                  {!isPublic && (
                    <div
                      style={{
                        position: "absolute",
                        right: "20px",
                        bottom: "20px",
                        zIndex: 1000,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <Button
                        data-tour="map-add-tree-btn"
                        variant="primary"
                        onClick={startAddTree}
                        disabled={isAddingTree || isEditMode}
                        style={{
                          ...mapFloatingPrimaryButtonStyle,
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        {t("addTree")}
                      </Button>
                    </div>
                  )}
                  {isAddingTree && addPosition && (
                    <PositionInputPanel
                      position={addPosition}
                      onChange={(latlng) => {
                        if (isPositionConfirmed) return;
                        ignoreNextFormSyncRef.current = true;
                        setAddPosition(latlng);
                        if (mapRef.current) {
                          mapRef.current.setView(latlng, mapRef.current.getZoom());
                        }
                      }}
                      onConfirm={(nextPosition) => {
                        if (nextPosition) {
                          ignoreNextFormSyncRef.current = true;
                          setAddPosition(nextPosition);
                        }
                        setIsPositionConfirmed(true);
                      }}
                      onCancel={cancelAddTree}
                      onUseMyLocation={handleUseMyLocation}
                      disabledConfirm={!addPosition || isPositionConfirmed}
                      locked={isPositionConfirmed}
                      mapRef={mapRef}
                    />
                  )}
                  {isEditMode && editPosition && !isEditPositionConfirmed && (
                    <PositionInputPanel
                      position={editPosition}
                      onChange={(latlng) => {
                        if (isEditPositionConfirmed) return;
                        ignoreNextFormSyncRef.current = true;
                        setEditPosition(latlng);
                        if (mapRef.current) {
                          mapRef.current.setView(latlng, mapRef.current.getZoom());
                        }
                      }}
                      onConfirm={(nextPosition) => {
                        if (nextPosition) {
                          ignoreNextFormSyncRef.current = true;
                          setEditPosition(nextPosition);
                        }
                        handleEditPositionConfirm();
                      }}
                      onCancel={cancelEditTree}
                      onUseMyLocation={handleUseMyLocation}
                      disabledConfirm={!editPosition || isEditPositionConfirmed}
                      locked={isEditPositionConfirmed}
                      mapRef={mapRef}
                    />
                  )}
                </div>
              </Col>

              {isAddingTree && addPosition && isPositionConfirmed && (
                <Col
                  ref={addDetailPanelRef}
                  lg="5"
                  className="tree-detail-column entity-detail-column"
                  style={{
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <TreeForm
                    show={true}
                    isUpdate={false}
                    initialValues={addFormInitialValues}
                    externalPosition={addPosition}
                    onPositionChange={handleFormPositionChange}
                    setOpenForm={setIsAddingTree}
                    setIsUpdate={noop}
                    setTreeValues={handleTreeCreated}
                    renderMode="inline"
                    inlineVariant="detail"
                    onCancel={cancelAddTree}
                    positionReadOnly={true}
                    renderTopContent={({ values, setFieldValue }) => (
                      <div style={{ marginBottom: "12px" }}>
                        <AreaProjectPickerField values={values} setFieldValue={setFieldValue} />
                      </div>
                    )}
                  />
                </Col>
              )}
              {!isAddingTree && editingTreeId && activeTree && (
                <Col
                  ref={editDetailPanelRef}
                  lg="5"
                  className="tree-detail-column entity-detail-column"
                  style={{
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <TreeForm
                    show={true}
                    isUpdate={true}
                    formValue={activeTreeForForm}
                    initialValues={null}
                    externalPosition={isEditPositionConfirmed ? editPosition : null}
                    onPositionChange={handleEditPositionChange}
                    setOpenForm={(nextOpen) => {
                      if (!nextOpen) {
                        cancelEditTree();
                      }
                    }}
                    setIsUpdate={noop}
                    setTreeValues={handleTreeCreated}
                    renderMode="inline"
                    inlineVariant="detail"
                    onCancel={cancelEditTree}
                    positionReadOnly={false}
                    renderTopContent={({ values, setFieldValue }) => (
                      <div style={{ marginBottom: "12px" }}>
                        <div className="tree-edit-position-summary mb-3">
                          <div className="tree-edit-position-summary__content">
                            <div className="tree-edit-position-summary__label">{t("position")}</div>
                            <div className="tree-edit-position-summary__value">
                              {`${formatCoordinateValue(
                                editPosition?.lat ?? values?.latitude,
                                6
                              )}, ${formatCoordinateValue(
                                editPosition?.lng ?? values?.longitude,
                                6
                              )}`}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="tree-edit-position-summary__button"
                            onClick={() => {
                              setIsEditPositionConfirmed(false);
                              if (mapRef.current) {
                                ignoreNextFormSyncRef.current = true;
                                const center = mapRef.current.getCenter();
                                setEditPosition({ lat: center.lat, lng: center.lng });
                              }
                            }}
                          >
                            {t("change_position")}
                          </Button>
                        </div>
                        <AreaProjectPickerField values={values} setFieldValue={setFieldValue} />
                      </div>
                    )}
                  />
                </Col>
              )}
              {!isAddingTree && !editingTreeId && activeTreeId && !useMapDetailDrawer && (
                <Col
                  lg={detailTreeColLg}
                  className="tree-detail-column entity-detail-column"
                  style={{
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <div className="entity-detail-height-shell" style={{ height: "100%" }}>
                    <TreeDetail
                      map={mapRef.current}
                      trees={trees}
                      treeId={activeTreeId}
                      pid={pidForDetail}
                      aid={aidForDetail}
                      type="map"
                      setOpenTreeDetail={setOpenTreePopup}
                      onSensorEditStateChange={setIsSensorEditOpen}
                      isPublic={isPublic}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Container>
          {useMapDetailDrawer && !isAddingTree && !editingTreeId && activeTreeId && (
            <Offcanvas
              show={Boolean(activeTreeId)}
              onHide={closeMapTreeDrawer}
              placement="end"
              backdrop={false}
              scroll={false}
              style={{
                width: "min(92vw, 560px)",
                top: topOffset,
                height: `calc(100dvh - ${topOffset})`,
                zIndex: 1020,
              }}
            >
              <Offcanvas.Body className="p-0">
                <TreeDetail
                  map={mapRef.current}
                  trees={trees}
                  treeId={activeTreeId}
                  pid={pidForDetail}
                  aid={aidForDetail}
                  type="map"
                  setOpenTreeDetail={setOpenTreePopup}
                  onSensorEditStateChange={setIsSensorEditOpen}
                  isPublic={isPublic}
                />
              </Offcanvas.Body>
            </Offcanvas>
          )}
        </div>
      )}
    </>
  );
}

export default StandaloneMapView;
